import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../auth/AuthContext';
import TeamOverviewComponent from '../components/TeamOverviewComponent';
import PlayerCardComponent from '../components/PlayerCardComponent';
import '../global.css';
import { fetchWithAuth } from '../utils/fetchWithAuth';

const EditTeamPage = () => {
  const { id } = useParams(); // Team ID
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [teamPlayerIds, setTeamPlayerIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!token) return;

    const fetchTeam = async () => {
      const res = await fetch(`https://vballbackend.onrender.com/api/team/${id}/players`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTeam({ id, team_name: data.team_name || 'Team' });
      setTeamPlayerIds(new Set(data.players.map(p => p.id)));
    };

    const fetchAllPlayers = async () => {
      const res = await fetch(`https://vballbackend.onrender.com/api/coach/players`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPlayers(data.players || []);
    };

    fetchTeam();
    fetchAllPlayers();
  }, [id, token]);

  const handleAddPlayer = async (playerId) => {
    await fetch(`https://vballbackend.onrender.com/api/team/${id}/add_player`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ player_id: playerId })
    });
    setTeamPlayerIds(prev => new Set([...prev, playerId]));
  };

  const handleRemovePlayer = async (playerId) => {
    const confirm = window.confirm("Are you sure you want to remove this player from the team?");
    if (!confirm) return;

    await fetch(`https://vballbackend.onrender.com/api/team/${id}/remove_player`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ player_id: playerId })
    });

    setTeamPlayerIds(prev => {
      const updated = new Set(prev);
      updated.delete(playerId);
      return updated;
    });
  };

  const filteredPlayers = players.filter(player =>
    player.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const playersInTeam = filteredPlayers.filter(p => teamPlayerIds.has(p.id));
  const playersNotInTeam = filteredPlayers.filter(p => !teamPlayerIds.has(p.id));

  return (
    <div className="container" style={{ paddingTop: '2rem' }}>
      <button onClick={() => navigate('/coachdashboard')} className="btn-secondary mb-4">
        ← Back to Dashboard
      </button>

      {team && <TeamOverviewComponent team={{ ...team, players: [] }} />}

      {/* Search bar */}
      <div className="my-4" style={{ width: '90%', margin: '0 auto' }}>
        <input
          type="text"
          placeholder="Search by player name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-grow"
          style={{
            padding: '0.5rem',
            border: '1px solid #D1D5DB',
            borderRadius: '0.375rem',
            fontSize: '1rem',
            width: '100%'
          }}
        />
      </div>

      {/* In Team Section */}
      <div className="mb-5" style={{ width: '90%', margin: '0 auto' }}>
        <h3 className="mb-3">In Team</h3>
        <div
          className="flex-row"
          style={{
            flexWrap: 'wrap',
            gap: '2rem',
            justifyContent: 'flex-start'
          }}
        >
          {playersInTeam.length === 0 ? (
            <div className="text-muted">No players currently in team.</div>
          ) : (
            playersInTeam.map(player => (
              <div
                key={player.id}
                className="card flex-col"
                style={{
                  width: '300px',
                  alignItems: 'center',
                  padding: '1rem',
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <PlayerCardComponent player={player} />
                <button
                  className="btn-secondary mt-2"
                  onClick={() => handleRemovePlayer(player.id)}
                >
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Not in Team Section */}
      <div className="mb-5" style={{ width: '90%', margin: '0 auto' }}>
        <h3 className="mb-3">Not in Team</h3>
        <div
          className="flex-row"
          style={{
            flexWrap: 'wrap',
            gap: '2rem',
            justifyContent: 'flex-start'
          }}
        >
          {playersNotInTeam.length === 0 ? (
            <div className="text-muted">No players available to add.</div>
          ) : (
            playersNotInTeam.map(player => (
              <div
                key={player.id}
                className="card flex-col"
                style={{
                  width: '300px',
                  alignItems: 'center',
                  padding: '1rem',
                  boxSizing: 'border-box',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <PlayerCardComponent player={player} />
                <button
                  className="btn-primary mt-2"
                  onClick={() => handleAddPlayer(player.id)}
                >
                  Add
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EditTeamPage;
